package com.bikompanion.data.parking

import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext
import com.bikompanion.data.cache.BikeDataCache
import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.BoundingBox
import com.bikompanion.domain.ParkingRepository
import kotlin.coroutines.cancellation.CancellationException
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.ExperimentalTime

/**
 * How long an area is trusted before it is worked out again.
 *
 * Racks are street furniture: one appears when a council installs it, which is a scale of
 * months. A week is short enough that new mapping shows up in reasonable time, and long enough
 * that the work of decoding an area happens once rather than on every visit to it.
 */
internal val ParkingCacheLifetime = 7.days

/**
 * Racks for an area, resolved once and then served from disk.
 *
 * Since racks come from the dataset shipped with the app ([BundledParkingRepository]) this is no
 * longer standing between the map and a rate-limited upstream, and it earns its place for two
 * smaller reasons: it keeps the decoded form of an area so a revisit costs nothing, and it holds
 * a last good answer for the fallback path, where the source really can fail.
 */
@OptIn(ExperimentalTime::class)
class ParkingRepositoryImpl(
    private val delegate: ParkingRepository,
    private val cache: BikeDataCache,
    /**
     * Where the store and the parsing run. Injected only so a test can pin them to its own
     * scheduler; nothing in the app passes anything but the default.
     */
    private val dispatcher: CoroutineDispatcher = Dispatchers.Default,
) : ParkingRepository {
    private val areaLocksGuard = Mutex()
    private val areaLocks = mutableMapOf<String, Mutex>()

    override suspend fun fetchBikeParking(bounds: BoundingBox): List<BikeParkingSpot> =
        // Off the caller's dispatcher, which is the main thread: the store is synchronous, and
        // reading plus decoding a whole area's racks there was long enough to drop frames while
        // the map was still being panned.
        withContext(dispatcher) {
            val key = bounds.cacheKey()
            fresh(key)?.let { return@withContext it }
            // One request per area at a time. The screen asks for overlapping ground from more
            // than one place — the layer toggle and the camera both start a load — and two trips
            // for the same cell is one more than Overpass will wear.
            areaLock(key).withLock {
                // Whoever held the lock has usually just filled this in.
                fresh(key)?.let { return@withLock it }
                try {
                    delegate.fetchBikeParking(bounds).also { cache.writeParking(key, it) }
                } catch (cancellation: CancellationException) {
                    throw cancellation
                } catch (error: Throwable) {
                    // Overpass answers a throttle or an overload with an HTML page, which the
                    // parser throws on rather than reading as "no racks" — see parseBikeParking.
                    // Stale racks are still racks, so an old copy beats an error; with nothing
                    // stored the failure has to travel, because an empty list here is
                    // indistinguishable from an area genuinely without racks and the caller
                    // records that as fetched and never asks again. A throttled first look would
                    // leave the map blank for the whole session.
                    cache.readParking(key)?.spots ?: throw error
                }
            }
        }

    /** What's stored for [key], if it is young enough to serve without asking again. */
    private fun fresh(key: String): List<BikeParkingSpot>? {
        val cached = cache.readParking(key) ?: return null
        val age = Clock.System.now().toEpochMilliseconds() - cached.updatedAtEpochMillis
        return cached.spots.takeIf { age < ParkingCacheLifetime.inWholeMilliseconds }
    }

    /**
     * Kept for the life of the repository rather than removed once released — an unlocked [Mutex]
     * is a few bytes, and there are only as many keys as cells a rider visits in a sitting.
     */
    private suspend fun areaLock(key: String): Mutex = areaLocksGuard.withLock { areaLocks.getOrPut(key) { Mutex() } }
}

/**
 * Rounded to about a hundred metres, so the same area asked for twice hits the same entry.
 *
 * Areas come from a network's coverage, which is computed from station positions and can shift
 * by metres as an operator adds a dock — enough to miss an exact-match key every time.
 */
private fun BoundingBox.cacheKey(): String =
    listOf(south, west, north, east)
        .joinToString(",") { coordinate -> ((coordinate * 1000).toLong()).toString() }
